import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { parivartanAPI } from '../../services/parivartanAPI';
import './MasterLookup.scss';

const norm = (value) => String(value || '').trim().toLowerCase();

export const matchOption = (option, value) => {
  const needle = norm(value);
  if (!needle) return false;
  if (norm(option.value) === needle || norm(option.label) === needle) return true;
  return (option.aliases || []).some((alias) => norm(alias) === needle);
};

export const resolveMasterValue = (options, value) => {
  if (!value) return value || '';
  const hit = (options || []).find((option) => matchOption(option, value));
  return hit ? hit.value : value;
};

export const optionsFromMasters = (rows) => (rows || [])
  .filter((row) => row && row.status !== 'inactive' && row.name)
  .map((row) => ({
    value: row.name,
    label: row.name,
    hint: row.code || row.shortName || '',
    aliases: [row.name, row.code, row.shortName].filter(Boolean)
  }))
  .sort((left, right) => left.label.localeCompare(right.label));

export function useMasterOptions() {
  const [state, setState] = useState({
    ready: false,
    customers: [],
    services: [],
    sectors: [],
    tenderTypes: [],
    sources: [],
    stages: [],
    priorities: [],
    urgencies: []
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await parivartanAPI.lookupMasters();
        const data = response.data?.data || {};
        if (cancelled) return;
        setState({
          ready: true,
          customers: optionsFromMasters(data.customers),
          services: optionsFromMasters(data.services),
          sectors: optionsFromMasters(data.sectors),
          tenderTypes: optionsFromMasters(data.tenderTypes),
          sources: optionsFromMasters(data.sources),
          stages: (data.stages || [])
            .filter((row) => row?.code && row?.label)
            .map((row) => ({ code: row.code, label: row.label })),
          priorities: (data.priorities || [])
            .filter((row) => row?.code && row?.label)
            .map((row) => ({ code: row.code, label: row.label })),
          urgencies: (data.urgencies || [])
            .filter((row) => row?.code && row?.label)
            .map((row) => ({ code: row.code, label: row.label }))
        });
      } catch {
        if (!cancelled) setState((prev) => ({ ...prev, ready: true }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}

const asList = (value, multiple) => {
  if (multiple) {
    if (Array.isArray(value)) return value.filter(Boolean);
    return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
  }
  return value ? [value] : [];
};

const MasterLookup = ({
  options = [],
  value = '',
  onChange,
  multiple = false,
  placeholder = 'Search or select',
  disabled = false,
  invalid = false,
  id
}) => {
  const autoId = useId();
  const listId = `${id || autoId}-list`;
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const selected = asList(value, multiple);
  const chosen = useMemo(() => {
    const extras = selected
      .filter((item) => !options.some((option) => matchOption(option, item)))
      .map((item) => ({ value: item, label: item, hint: 'Current value', aliases: [item] }));
    return [...extras, ...options];
  }, [options, selected]);

  const filtered = useMemo(() => {
    const needle = norm(query);
    if (!needle) return chosen;
    return chosen.filter((option) => {
      const haystack = [option.label, option.hint, ...(option.aliases || [])].join(' ');
      return norm(haystack).includes(needle);
    });
  }, [chosen, query]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  const displayValue = () => {
    if (open || multiple) return query;
    const hit = chosen.find((option) => matchOption(option, value));
    return hit ? hit.label : (value || '');
  };

  const choose = (option) => {
    if (multiple) {
      const exists = selected.some((item) => norm(item) === norm(option.value));
      const next = exists
        ? selected.filter((item) => norm(item) !== norm(option.value))
        : [...selected, option.value];
      onChange(next);
      setQuery('');
      inputRef.current?.focus();
      return;
    }
    onChange(option.value);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlight((index) => Math.min(index + 1, Math.max(filtered.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      if (open && filtered[highlight]) {
        event.preventDefault();
        choose(filtered[highlight]);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
      setQuery('');
    } else if (event.key === 'Backspace' && multiple && !query && selected.length) {
      onChange(selected.slice(0, -1));
    }
  };

  return (
    <div
      ref={rootRef}
      className={`master-lookup${open ? ' is-open' : ''}${invalid ? ' is-invalid' : ''}`}
    >
      <div className="master-lookup__control" onMouseDown={() => { if (!disabled) inputRef.current?.focus(); }}>
        {multiple && selected.length ? (
          <span className="master-lookup__chips">
            {selected.map((item) => {
              const hit = chosen.find((option) => matchOption(option, item));
              return (
                <span key={item} className="master-lookup__chip">
                  {hit?.label || item}
                  <button
                    type="button"
                    aria-label={`Remove ${hit?.label || item}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onChange(selected.filter((entry) => norm(entry) !== norm(item)))}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </span>
        ) : null}
        <input
          ref={inputRef}
          id={id}
          className="master-lookup__input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          disabled={disabled}
          placeholder={selected.length && !open ? '' : placeholder}
          value={displayValue()}
          onFocus={() => {
            if (disabled) return;
            setOpen(true);
            setQuery('');
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
            if (!multiple && event.target.value === '') onChange('');
          }}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          className="master-lookup__toggle"
          aria-label={open ? 'Close list' : 'Open list'}
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setOpen((current) => !current);
            setQuery('');
            inputRef.current?.focus();
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M2.5 4.25L6 7.75L9.5 4.25" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {open ? (
        <ul className="master-lookup__menu" id={listId} role="listbox">
          {filtered.length ? filtered.map((option, index) => {
            const isChosen = selected.some((item) => matchOption(option, item));
            return (
              <li
                key={`${option.value}-${option.hint || index}`}
                role="option"
                aria-selected={isChosen}
                className={`master-lookup__option${index === highlight ? ' is-active' : ''}${isChosen ? ' is-chosen' : ''}`}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(option)}
              >
                <span>{option.label}</span>
                {option.hint ? <span className="master-lookup__hint">{option.hint}</span> : null}
              </li>
            );
          }) : (
            <li className="master-lookup__empty">No matching records</li>
          )}
        </ul>
      ) : null}
    </div>
  );
};

export default MasterLookup;
